'use client'

import { useRef, type CSSProperties, type ReactNode } from 'react'
import { cx } from '../utils/cx'

export function AVPlayer({ children, className }: { children?: ReactNode; className?: string }) {
  return <div className={cx('st-av-player', className)}>{children}</div>
}

interface AVStageProps {
  kind?: 'video' | 'audio'
  playing?: boolean
  paused?: boolean
  vfake?: { big: ReactNode; sub?: ReactNode }
  // Real media element (or any node) rendered behind the play overlay.
  // For audio, the visible waveform still renders on top of this slot.
  children?: ReactNode
  showOverlay?: boolean
  onTogglePlay?: () => void
  style?: CSSProperties
  className?: string
}

export function AVStage({
  kind = 'video',
  playing,
  paused,
  vfake,
  children,
  showOverlay = true,
  onTogglePlay,
  style,
  className
}: AVStageProps) {
  const stageCls = cx('st-av-stage', kind === 'audio' && 'audio', playing && 'playing', paused && 'paused', className)
  return (
    <div className={stageCls} style={style}>
      {children}
      {kind === 'audio' ? (
        <div className="wave">
          {[...Array(40)].map((_, i) => (
            <i key={i} style={{ animationDelay: `${-(i * 0.08)}s` }} />
          ))}
        </div>
      ) : vfake ? (
        <div className="vfake">
          <div className="big">{vfake.big}</div>
          {vfake.sub && <div className="sub">{vfake.sub}</div>}
        </div>
      ) : null}
      {showOverlay && (
        <div className="play-overlay" onClick={onTogglePlay}>
          <div className="btn">
            <svg viewBox="0 0 24 24" fill="#0a0a0a">
              <path d="M7 4 L20 12 L7 20 Z" />
            </svg>
          </div>
        </div>
      )}
    </div>
  )
}

interface AVControlsProps {
  playing?: boolean
  onTogglePlay?: () => void
  pct?: number
  onSeek?: (pct: number) => void
  time?: ReactNode
  markers?: number[]
  onMarkerClick?: (index: number, at: number) => void
  className?: string
}

export function AVControls({
  playing,
  onTogglePlay,
  pct = 0,
  onSeek,
  time,
  markers = [],
  onMarkerClick,
  className
}: AVControlsProps) {
  const trackRef = useRef<HTMLDivElement | null>(null)
  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onSeek || !trackRef.current) return
    const r = trackRef.current.getBoundingClientRect()
    onSeek(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)))
  }
  return (
    <div className={cx('st-av-controls', className)}>
      <button type="button" className="playbtn" onClick={onTogglePlay} aria-label={playing ? 'Pause' : 'Play'}>
        {playing ? (
          <svg viewBox="0 0 24 24" fill="#0a0a0a">
            <rect x="6" y="4" width="4" height="16" />
            <rect x="14" y="4" width="4" height="16" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="#0a0a0a">
            <path d="M7 4 L20 12 L7 20 Z" />
          </svg>
        )}
      </button>
      <div className="st-scrubber" ref={trackRef} onClick={seek}>
        <div className="fill" style={{ width: `${pct * 100}%` }} />
        {markers.map((m, i) => (
          <div
            key={i}
            className="ts-marker"
            style={{ left: `${m * 100}%` }}
            onClick={(e) => {
              e.stopPropagation()
              onMarkerClick?.(i, m)
            }}
            title="Time-stamped comment"
          />
        ))}
      </div>
      {time && <span className="time">{time}</span>}
    </div>
  )
}

export function AVInfo({
  name,
  sub,
  children,
  className
}: {
  name?: ReactNode
  sub?: ReactNode
  children?: ReactNode
  className?: string
}) {
  return (
    <div className={cx('st-av-info', className)}>
      <div>
        <div className="name">{name}</div>
        {sub && <div className="sub">{sub}</div>}
      </div>
      {children}
    </div>
  )
}
